import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, PageHeader } from "../../../components/ui";
import { DeliveryChallanTable } from "../components/DeliveryChallanTable";
import { AddInvoiceNumberModal } from "../components/AddInvoiceNumberModal";
import { InventoryActionToast } from "../components/InventoryActionToast";
import { inventoryManagementService } from "../../../services/inventoryManagementService";

const PAGE_SIZE = 10;

export function DeliveryChallanPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const [toast, setToast] = useState(null);

  const loadRows = useCallback(() => {
    let active = true;
    setLoading(true);
    setError("");

    inventoryManagementService
      .getDeliveryChallans({
        page: page - 1,
        size: PAGE_SIZE,
      })
      .then((res) => {
        if (!active) return;
        setRows(res.content || []);
        setTotalItems(res.totalElements ?? (res.content || []).length);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load delivery challans:", err);
        setRows([]);
        setTotalItems(0);
        setError(t("validation.failedToRetrieveDeliveryChallans"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [page, reloadKey, t]);

  useEffect(() => {
    const cleanup = loadRows();
    return cleanup;
  }, [loadRows]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleSaveInvoice = async (invoiceNumber) => {
    if (!editing) return;
    setSaving(true);
    setSaveError("");
    try {
      await inventoryManagementService.updateDeliveryChallan(
        editing.deliveryChallanId,
        { ...editing, invoiceNumber }
      );
      setEditing(null);
      setToast({
        type: "success",
        message: t("inventory.invoiceNumberUpdated"),
      });
      setReloadKey((key) => key + 1);
    } catch (err) {
      console.error("Failed to update delivery challan:", err);
      setSaveError(
        err?.message || t("validation.deliveryChallanUpdateError")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (row) => {
    if (!row || downloadingId != null) return;
    const hasJournal = row.stockJournalId != null;
    const hasAllocation = row.customerAllocationId != null;
    if (!hasJournal && !hasAllocation) {
      setToast({
        type: "error",
        message: t("inventory.challanDownloadUnavailable"),
      });
      return;
    }

    // One PDF per table row: use this row's journal when present.
    const downloadRef = hasJournal
      ? { stockJournalId: row.stockJournalId }
      : { customerAllocationId: row.customerAllocationId };

    setDownloadingId(row.deliveryChallanId);
    try {
      await inventoryManagementService.downloadChallan(downloadRef, {
        fallbackFilename: `delivery-challan-${row.deliveryChallanId}.pdf`,
      });
    } catch (err) {
      console.error("Failed to download delivery challan:", err);
      setToast({
        type: "error",
        message: err?.message || t("validation.challanDownloadError"),
      });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <AppLayout hideBreadcrumb className="space-y-6">
      <PageHeader
        back={
          <PageBackLink
            to="/inventory-management/stock-position"
            label={t("formActions.back")}
          />
        }
        title={t("inventory.deliveryChallan")}
      />
      <ContentCard className="overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
          <span className="text-xs font-bold text-foreground">
            {t("inventory.deliveryChallansOverview")}
          </span>
        </div>

        {loading ? (
          <div className="p-6 space-y-4 animate-pulse">
            <div className="h-10 bg-gray-100 rounded-lg w-full" />
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((idx) => (
                <div
                  key={idx}
                  className="h-12 bg-gray-50/50 rounded-lg w-full flex items-center gap-4 px-4"
                >
                  <div className="h-4 bg-gray-100 rounded w-20" />
                  <div className="h-4 bg-gray-100 rounded w-24" />
                  <div className="h-4 bg-gray-100 rounded w-1/4" />
                  <div className="h-4 bg-gray-100 rounded w-12" />
                  <div className="h-4 bg-gray-100 rounded w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-error font-semibold text-sm">
            ⚠️ {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-center font-sans">
            <div className="h-12 w-12 rounded-full bg-workspace per-file-hint grid place-items-center mb-4">
              <span>📦</span>
            </div>
            <h4 className="text-sm font-bold text-foreground">
              {t("inventory.noDeliveryChallansFound")}
            </h4>
            <p className="text-xs per-file-hint mt-1.5 leading-relaxed max-w-sm">
              {t("inventory.noDeliveryChallansHint")}
            </p>
          </div>
        ) : (
          <DeliveryChallanTable
            data={rows}
            page={page}
            pageSize={PAGE_SIZE}
            totalItems={totalItems}
            onPageChange={setPage}
            downloadingId={downloadingId}
            onEdit={(row) => {
              setSaveError("");
              setEditing(row);
            }}
            onDownload={handleDownload}
          />
        )}
      </ContentCard>

      <AddInvoiceNumberModal
        open={Boolean(editing)}
        challan={editing}
        submitting={saving}
        submitError={saveError}
        onOpenChange={(open) => {
          if (saving) return;
          if (!open) {
            setEditing(null);
            setSaveError("");
          }
        }}
        onSubmit={handleSaveInvoice}
      />

      {toast ? <InventoryActionToast toast={toast} /> : null}
    </AppLayout>
  );
}
